import React from 'react';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import EastRoundedIcon from '@mui/icons-material/EastRounded';
import { Box, Card, CardContent, Typography } from '@mui/material';
import type { ProliferatorMode } from '../../../catalog';
import { formatProliferatorMode } from '../../../i18n';
import type { PresentationModel } from '../../../presentation';
import { EntityIcon } from '../../shared/EntityIcon';
import { buildRecipePlanRevealKey } from '../../shared/recipePlanReveal';
import {
  RecipePlanAuxiliaryInput,
  RecipePlanFlowSequence,
  RecipePlanToggleButton,
  RecipePlanToggleGroup,
} from './RecipePlanCardParts';
import { useCatalog } from '../CatalogContext';
import { useWorkbench } from '../WorkbenchContext';
import { useWorkbenchDraft } from '../WorkbenchDraftContext';
import { buildRecipePlanCardDisplayModel, buildRecipeProliferatorLevelChoices, filterRecipeBuildingChoices } from '../workbenchHelpers';
import { recipePlanNumberSx } from '../workbenchStyles';

export interface RecipePlanCardProps {
  plan: PresentationModel['recipePlans'][number];
  numberLabel: string;
  numberTitle: string;
}

const BUILDING_CONTROL_LABEL = '\u5efa\u7b51\u9009\u62e9';
const PROLIFERATOR_MODE_CONTROL_LABEL = '\u589e\u4ea7\u7b56\u7565';
const PROLIFERATOR_LEVEL_CONTROL_LABEL = '\u589e\u4ea7\u7b49\u7ea7';
const NONE_OPTION_LABEL = '\u2205';

const RecipePlanCard = React.memo(function RecipePlanCard({ plan, numberLabel, numberTitle }: RecipePlanCardProps) {
  const {
    bundle,
    locale,
    iconAtlasIds,
    catalog,
    getRecipeBuildingOptions,
    getRecipeModeOptions,
    getRecipeLevelOptions,
  } = useCatalog();
  const {
    recipePreferences,
    recipeStrategyOverrides,
    preferredBuildings,
    disabledBuildingIds,
    parsedOverrides,
    applyRecipeStrategyPatch,
    setRecipePreferredBuilding,
    setRecipePreferredProliferator,
  } = useWorkbench();
  const {
    revealedRecipePlanKey,
    revealedRecipePlanNonce,
  } = useWorkbenchDraft();

  const displayModel = buildRecipePlanCardDisplayModel(catalog, plan, locale);
  const recipePlanRevealKey = buildRecipePlanRevealKey(plan);
  const isRevealed = recipePlanRevealKey === revealedRecipePlanKey;
  const cardRef = React.useRef<HTMLDivElement | null>(null);
  const buildingChoices = filterRecipeBuildingChoices(getRecipeBuildingOptions(plan.recipeId),
    disabledBuildingIds, parsedOverrides.value.disabledBuildingIds);
  const modeChoices = getRecipeModeOptions(plan.recipeId);
  const levelChoices = buildRecipeProliferatorLevelChoices(catalog, getRecipeLevelOptions(plan.recipeId), bundle.solveRequest.levelPrefix);
  const preference = recipePreferences.find(entry => entry.recipeId === plan.recipeId);
  const override = recipeStrategyOverrides.find(entry => entry.recipeId === plan.recipeId);
  const preferredBuilding = preferredBuildings.find(entry => entry.recipeId === plan.recipeId);
  const selectedBuildingId =
    override?.forcedBuildingId ||
    preferredBuilding?.buildingId ||
    preference?.preferredBuildingId ||
    '';
  const selectedMode =
    override?.forcedProliferatorMode || preference?.preferredProliferatorMode || '';
  const selectedLevelValue =
    typeof override?.forcedProliferatorLevel === 'number'
      ? override.forcedProliferatorLevel
      : typeof preference?.preferredProliferatorLevel === 'number'
        ? preference.preferredProliferatorLevel
        : '';
  const selectedLevel =
    selectedMode === 'none'
      ? ''
      : typeof selectedLevelValue === 'number' && selectedLevelValue > 0
        ? String(selectedLevelValue)
        : '';
  const levelSelectDisabled =
    levelChoices.length === 0 || selectedMode === 'none';

  React.useEffect(() => {
    if (!isRevealed || revealedRecipePlanNonce === 0) {
      return;
    }

    cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [isRevealed, revealedRecipePlanNonce]);

  return (
    <Card
      ref={cardRef}
      data-testid="recipe-plan-card"
      sx={{
        borderRadius: '10px',
        border: '1px solid',
        borderColor: isRevealed ? 'warning.main' : 'divider',
        boxShadow: isRevealed ? '0 0 0 2px rgba(244, 194, 102, 0.28)' : 'none',
        backgroundColor: isRevealed ? 'rgba(255, 246, 220, 0.92)' : '#fff',
        transition: 'border-color 140ms ease',
        '&:hover': { borderColor: isRevealed ? 'warning.main' : '#bccce9' },
        overflow: 'hidden',
        scrollMarginTop: 24,
        contentVisibility: 'auto',
        containIntrinsicSize: '128px 480px',
        contain: 'layout paint style',
      }}
    >
      <CardContent
        sx={{
          display: 'grid',
          gap: 1.25,
          p: 2,
          borderRadius: '10px',
          '&:last-child': { pb: 2 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: { xs: 'stretch', md: 'center' },
            justifyContent: 'space-between',
            gap: 1,
            flexWrap: 'wrap',
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              flex: '1 1 400px',
              minWidth: 0,
              display: 'flex',
              alignItems: { xs: 'stretch', sm: 'center' },
              gap: 0.875,
              flexWrap: 'wrap',
            }}
          >
            <Typography component="span" title={numberTitle} sx={recipePlanNumberSx}>
              {numberLabel}
            </Typography>
            <Typography
              variant="subtitle1"
              fontWeight={700}
              noWrap
              title={plan.recipeName}
              sx={{ minWidth: 0, flex: '0 1 auto' }}
            >
              {plan.recipeName}
            </Typography>

            <Box
              sx={{
                flex: '1 1 200px',
                minWidth: 0,
                ml: 'auto',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-start',
                flexWrap: 'wrap',
                rowGap: 0.25,
                columnGap: 0.625,
              }}
            >
              {buildingChoices.length > 1 && <RecipePlanToggleGroup
                ariaLabel={`${plan.recipeName} ${BUILDING_CONTROL_LABEL}`}
                value={selectedBuildingId}
                testId="recipe-plan-building-toggle-group"
                onChange={nextBuildingId => {
                  const accepted = applyRecipeStrategyPatch(plan.recipeId, {
                    forcedBuildingId: nextBuildingId,
                  });
                  if (accepted) {
                    setRecipePreferredBuilding(plan.recipeId, nextBuildingId);
                  }
                }}
              >
                {buildingChoices.map(building => (
                  <RecipePlanToggleButton
                    key={building.buildingId}
                    value={building.buildingId}
                    ariaLabel={`${plan.recipeName} ${building.name}`}
                    title={`${building.name} · 再次点击取消指定`}
                    iconOnly
                    testId={`recipe-plan-building-toggle-${building.buildingId}`}
                  >
                    <EntityIcon
                      label={building.name}
                      iconKey={building.icon}
                      atlasIds={iconAtlasIds}
                      size={18}
                    />
                  </RecipePlanToggleButton>
                ))}
              </RecipePlanToggleGroup>}

              {modeChoices.length > 0 && (
              <RecipePlanToggleGroup
                ariaLabel={`${plan.recipeName} ${PROLIFERATOR_MODE_CONTROL_LABEL}`}
                value={selectedMode}
                testId="recipe-plan-mode-toggle-group"
                onChange={nextValue => {
                  const nextMode = nextValue as '' | ProliferatorMode;
                  const nextLevel =
                    nextMode === 'none'
                      ? 0
                      : typeof selectedLevelValue === 'number' && selectedLevelValue > 0
                        ? selectedLevelValue
                        : '';
                  const accepted = applyRecipeStrategyPatch(plan.recipeId, {
                    forcedProliferatorMode: nextMode,
                    forcedProliferatorLevel: nextLevel,
                  });
                  if (accepted) {
                    setRecipePreferredProliferator(plan.recipeId, nextMode, nextLevel);
                  }
                }}
              >
                {modeChoices.map(mode => (
                  <RecipePlanToggleButton
                    key={mode}
                    value={mode}
                    ariaLabel={`${plan.recipeName} ${formatProliferatorMode(mode, locale)}`}
                    title={`${formatProliferatorMode(mode, locale)} · 再次点击取消指定`}
                    testId={`recipe-plan-mode-toggle-${mode}`}
                  >
                    {mode === 'none' ? NONE_OPTION_LABEL : formatProliferatorMode(mode, locale)}
                  </RecipePlanToggleButton>
                ))}
              </RecipePlanToggleGroup>
              )}

              {modeChoices.length > 0 && levelChoices.length > 1 && !levelSelectDisabled && (
              <RecipePlanToggleGroup
                ariaLabel={`${plan.recipeName} ${PROLIFERATOR_LEVEL_CONTROL_LABEL}`}
                value={selectedLevel}
                testId="recipe-plan-level-toggle-group"
                onChange={nextValue => {
                  const nextLevel = nextValue ? Number(nextValue) : '';
                  const accepted = applyRecipeStrategyPatch(plan.recipeId, {
                    forcedProliferatorLevel: nextLevel,
                  });
                  if (accepted) {
                    setRecipePreferredProliferator(plan.recipeId, selectedMode, nextLevel);
                  }
                }}
              >
                {levelChoices.map(level => (
                  <RecipePlanToggleButton
                    key={level.level}
                    value={String(level.level)}
                    ariaLabel={`${plan.recipeName} ${bundle.solveRequest.levelPrefix} ${level.level}`}
                    title={`${level.label} · 再次点击取消指定`}
                    iconOnly
                    testId={`recipe-plan-level-toggle-${level.level}`}
                  >
                    {level.iconKey ? <EntityIcon label={level.label} iconKey={level.iconKey} atlasIds={iconAtlasIds} size={18} /> : level.level}
                  </RecipePlanToggleButton>
                ))}
              </RecipePlanToggleGroup>
              )}
            </Box>
          </Box>

          <Box
            sx={{
              flex: '0 0 auto',
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
              gap: 1,
              flexWrap: 'wrap',
              color: 'text.secondary',
            }}
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
              <EntityIcon
                label={plan.buildingName}
                iconKey={plan.buildingIconKey}
                atlasIds={iconAtlasIds}
                size={18}
              />
              <Typography variant="caption" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                {displayModel.buildingCountLabel}
              </Typography>
            </Box>
            <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.375 }}>
              <BoltRoundedIcon sx={{ fontSize: 16 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                {displayModel.powerLabel}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.625,
            flexWrap: 'wrap',
            borderRadius: '16px',
            px: 1.25,
            py: 0.875,
            backgroundColor: 'rgba(22, 54, 89, 0.035)',
          }}
        >
          <Box
            sx={{
              flex: '1 1 320px',
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 0.625,
              flexWrap: 'wrap',
            }}
          >
            <RecipePlanFlowSequence
              items={displayModel.visibleInputs}
              locale={locale}
              atlasIds={iconAtlasIds}
              noneText={bundle.common.none}
              powerItemId={catalog?.powerItemId}
            />
            <EastRoundedIcon sx={{ color: 'text.secondary', fontSize: 20, flexShrink: 0 }} />
            <RecipePlanFlowSequence
              items={displayModel.outputs}
              locale={locale}
              atlasIds={iconAtlasIds}
              noneText={bundle.common.none}
              powerItemId={catalog?.powerItemId}
            />
            <RecipePlanAuxiliaryInput
              item={displayModel.auxiliaryProliferatorInput}
              locale={locale}
              atlasIds={iconAtlasIds}
              powerItemId={catalog?.powerItemId}
            />
          </Box>
          <Typography
            variant="caption"
            sx={{
              flex: '0 0 auto',
              ml: 'auto',
              color: 'text.secondary',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {displayModel.proliferatorLabel}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
});

export default RecipePlanCard;
